import { computed, onMounted, ref } from 'vue';
import { parseApiResponse } from '../utils/api.js';

export function createAdminPage({ dayjs, message }) {
  return {
    setup() {
      const loading = ref(false);
      const submissions = ref([]);
      const search = ref('');
      const pagination = ref({
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      });

      const columns = [
        {
          title: 'Канал',
          dataIndex: 'source_channel',
          key: 'source_channel',
          width: 120,
        },
        {
          title: 'Реферер',
          dataIndex: 'referrer_full_name',
          key: 'referrer_full_name',
        },
        {
          title: 'Телефон',
          dataIndex: 'referrer_phone',
          key: 'referrer_phone',
        },
        {
          title: 'Рефералов',
          dataIndex: 'referrals_count',
          key: 'referrals_count',
          width: 120,
        },
        {
          title: 'Дата',
          dataIndex: 'submitted_at',
          key: 'submitted_at',
          customRender: ({ text }) => dayjs(text).format('DD.MM.YYYY HH:mm'),
        },
      ];

      const filteredSubmissions = computed(() => {
        const query = search.value.trim().toLowerCase();
        if (!query) {
          return submissions.value;
        }

        return submissions.value.filter((item) => {
          const referrerMatches = [
            item.referrer_full_name,
            item.referrer_phone,
          ].some((value) => String(value || '').toLowerCase().includes(query));

          const referralMatches = (item.referrals || []).some((referral) =>
            [referral.full_name, referral.phone].some((value) =>
              String(value || '').toLowerCase().includes(query)
            )
          );

          return referrerMatches || referralMatches;
        });
      });

      const stats = computed(() => ({
        submissions: filteredSubmissions.value.length,
        referrals: filteredSubmissions.value.reduce((total, item) => total + item.referrals_count, 0),
      }));

      const loadSubmissions = async () => {
        loading.value = true;

        try {
          const response = await fetch('/api/admin/submissions');
          const result = await parseApiResponse(response);

          if (result.__nonJson) {
            throw new Error(`Сервер вернул не JSON, а ${response.status} ${response.statusText}. Проверь /api/admin/submissions.`);
          }

          if (!response.ok) {
            throw new Error(result.message || 'Не удалось загрузить заявки.');
          }

          submissions.value = result.data;
        } catch (error) {
          message.error(error.message || 'Не удалось загрузить заявки.');
        } finally {
          loading.value = false;
        }
      };

      const handleTableChange = (pager) => {
        pagination.value = {
          ...pagination.value,
          current: pager.current || 1,
          pageSize: pager.pageSize || pagination.value.pageSize,
        };
      };

      onMounted(loadSubmissions);

      return {
        columns,
        filteredSubmissions,
        handleTableChange,
        loading,
        loadSubmissions,
        pagination,
        search,
        stats,
        submissions,
      };
    },
    template: `
      <div class="page-shell admin-shell">
        <div class="hero-card admin-hero">
          <div class="hero-copy">
            <span class="eyebrow">Admin</span>
            <h1>Заявки по реферальной программе</h1>
            <p>
              Здесь можно быстро посмотреть, кто отправил заявку, сколько рефералов в ней было
              и какие контакты сохранились в системе.
            </p>
          </div>
          <div class="stats-grid">
            <div class="stat-tile">
              <strong>{{ stats.submissions }}</strong>
              <span>заявок</span>
            </div>
            <div class="stat-tile">
              <strong>{{ stats.referrals }}</strong>
              <span>рефералов</span>
            </div>
          </div>
        </div>

        <a-card class="glass-card admin-table-card" :bordered="false">
          <template #title>Список заявок</template>
          <template #extra>
            <div class="admin-actions">
              <a href="/" class="inline-link">К публичной форме</a>
              <a-button type="default" @click="loadSubmissions" :loading="loading">Обновить</a-button>
            </div>
          </template>

          <div class="admin-search">
            <a-input
              v-model:value="search"
              size="large"
              placeholder="Поиск по ФИО или телефону реферера и рефералов"
              allow-clear
            />
          </div>

          <a-table
            :columns="columns"
            :data-source="filteredSubmissions"
            :loading="loading"
            :pagination="pagination"
            row-key="submission_id"
            :scroll="{ x: 720 }"
            @change="handleTableChange"
          >
            <template #expandedRowRender="{ record }">
              <div class="expanded-row">
                <h3>Рефералы</h3>
                <div v-if="record.referrals.length" class="referral-preview-list">
                  <div v-for="referral in record.referrals" :key="referral.id" class="referral-preview-item">
                    <strong>{{ referral.full_name }}</strong>
                    <span>{{ referral.phone }}</span>
                  </div>
                </div>
                <a-empty v-else description="Нет связанных рефералов" />
              </div>
            </template>
          </a-table>
        </a-card>
      </div>
    `,
  };
}
